import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useAccountParams } from '~/hooks/useAccountParams';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import type { AnalyticsMode } from './analyticsMode';
import { buildAnalyticsStatsHref, type AnalyticsStatsSearchMode } from './analyticsStatsUrlUtils';

type AnalyticsStatsContextValue = {
  mode: AnalyticsMode;
  accountType: 'client' | 'partner';
  accountId: number;
  baseStatsPath: string;
  showSchedules: boolean;
  /** When true, analytics is embedded in Staff Client Manager (no duplicate page title/container). */
  embeddedInClientManager: boolean;
  /** Staff authoring: pre-seed stat set query param; null when live or client mode. */
  authoringStatSet: Genie2ReportingMode | null;
};

const AnalyticsStatsContext = createContext<AnalyticsStatsContextValue | null>(null);

type AnalyticsProviderProps = {
  mode: AnalyticsMode;
  /** Pre-seed stat set for staff authoring; omit on client routes. */
  authoringStatSet?: Genie2ReportingMode | null;
  children: React.ReactNode;
};

function buildBaseStatsPath(
  mode: AnalyticsMode,
  accountType: 'client' | 'partner',
  accountId: number,
): string {
  const statsSegment = `/${accountType}/${accountId}/analytics/stats`;
  return mode === 'authoring' ? `/staff${statsSegment}` : statsSegment;
}

export function AnalyticsProvider({
  mode,
  authoringStatSet = null,
  children,
}: AnalyticsProviderProps) {
  const params = useAccountParams();
  const accountId = params?.accountId ?? 0;
  const accountType = (params?.accountType ?? 'client') as 'client' | 'partner';

  const value = useMemo(
    (): AnalyticsStatsContextValue => ({
      mode,
      accountType,
      accountId,
      baseStatsPath: buildBaseStatsPath(mode, accountType, accountId),
      showSchedules: mode === 'client',
      embeddedInClientManager: mode === 'authoring',
      authoringStatSet: mode === 'authoring' ? authoringStatSet : null,
    }),
    [mode, accountType, accountId, authoringStatSet],
  );

  return <AnalyticsStatsContext.Provider value={value}>{children}</AnalyticsStatsContext.Provider>;
}

export function useAnalyticsStatsContext(): AnalyticsStatsContextValue {
  const context = useContext(AnalyticsStatsContext);
  if (!context) {
    throw new Error('useAnalyticsStatsContext must be used within AnalyticsProvider');
  }
  return context;
}

/** Safe default for hooks used outside AnalyticsProvider (e.g. staff limit editor prefetch). */
export function useAnalyticsStatsContextOrDefault(): AnalyticsStatsContextValue {
  const context = useContext(AnalyticsStatsContext);
  const params = useAccountParams();
  const accountId = params?.accountId ?? 0;
  const accountType = (params?.accountType ?? 'client') as 'client' | 'partner';

  return useMemo(() => {
    if (context) return context;
    return {
      mode: 'client' as const,
      accountType,
      accountId,
      baseStatsPath: buildBaseStatsPath('client', accountType, accountId),
      showSchedules: true,
      embeddedInClientManager: false,
      authoringStatSet: null,
    };
  }, [context, accountType, accountId]);
}

export function useAnalyticsStatsBasePath(): string {
  return useAnalyticsStatsContext().baseStatsPath;
}

/**
 * Build in-app analytics stats links that preserve URL filter params (and optionally home tab/scope).
 *
 * @param relativePath - Path after `baseStatsPath` (e.g. `/view/sv_1`) or a full stats path
 */
export function useAnalyticsStatsHref() {
  const { baseStatsPath } = useAnalyticsStatsContext();
  const { search } = useLocation();

  const buildHref = useCallback(
    (relativePath: string, mode?: AnalyticsStatsSearchMode, setParams?: Record<string, string>) => {
      const resolvedMode = mode ?? 'filters';
      const path = relativePath.startsWith(baseStatsPath)
        ? relativePath
        : `${baseStatsPath}${relativePath.startsWith('/') ? relativePath : `/${relativePath}`}`;
      return buildAnalyticsStatsHref(path, search, resolvedMode, setParams);
    },
    [baseStatsPath, search],
  );

  const buildHomeHref = useCallback(() => buildHref('', 'home'), [buildHref]);

  /** Schedule routes do not inherit report-page filter query params. */
  const buildScheduleHref = useCallback(
    (relativePath: string) => {
      return relativePath.startsWith(baseStatsPath)
        ? relativePath
        : `${baseStatsPath}${relativePath.startsWith('/') ? relativePath : `/${relativePath}`}`;
    },
    [baseStatsPath],
  );

  return { buildHref, buildHomeHref, buildScheduleHref, search };
}
